import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import type { CurrencyApi } from "@/types/api/companies";

/**
 * Mock currencies data for demo mode
 */
const mockCurrencies: CurrencyApi[] = [
  { id: 1, code: 'USD', name: 'US Dollar', symbol: '$' },
  { id: 2, code: 'EUR', name: 'Euro', symbol: '€' },
  { id: 3, code: 'GBP', name: 'British Pound', symbol: '£' },
  { id: 4, code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
  { id: 5, code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { id: 6, code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { id: 7, code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
];

interface CurrencyNode {
  currencyId: number;
  code: string;
  symbol: string | null;
  name: string | null;
}

interface CurrencyListGraphQLResponse {
  currencies: {
    currencyList: {
      edges: Array<{ node: CurrencyNode }>;
    };
  };
}

const CURRENCY_LIST_QUERY = `
  query CurrencyList($first: Int) {
    currencies {
      currencyList(first: $first) {
        edges {
          node {
            currencyId
            code
            symbol
            name
          }
        }
      }
    }
  }
`;

function mapNodeToCurrencyApi(node: CurrencyNode): CurrencyApi {
  return {
    id: node.currencyId,
    code: node.code,
    name: node.name ?? node.code,
    symbol: node.symbol,
  };
}

/**
 * Hook to fetch currencies list from the rapid read GraphQL endpoint
 * Respects demo/live mode - uses mock data in demo mode, GraphQL in live mode
 */
export function useCurrenciesList() {
  const apiConfig = useApiConfig();

  return useModeAwareQuery<CurrencyApi[], Error>(
    ["currencies"],
    async ({ signal }) => {
      const response = await graphqlQuery<CurrencyListGraphQLResponse>(
        apiConfig,
        CURRENCY_LIST_QUERY,
        { first: 0 },
        signal,
      );

      return response.currencies.currencyList.edges.map((x) =>
        mapNodeToCurrencyApi(x.node),
      );
    },
    mockCurrencies,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes - currencies don't change often
    }
  );
}
